// FAQ Platform FAQ Data
// Version 2
// September 16, 2026
//
// Unlike the CHCA and Outdoor Lighting FAQs, this one is not transcribed from an
// outside petitioner's source document — it is Michael Rubenstein's own
// description of the FAQ hosting platform, written for petitioners who might
// want a FAQ page of their own.
//
// The Overview ends with a download button for the submission template, which
// lives in frontend/public/documents/ and is served statically. Only the latest
// version is published: when a new one is issued, add it, point `template`
// below at it, and delete the previous file, so nobody can reach a superseded
// template. Older versions stay in git history and in the Drive folder.
//
// Question `id` values are permanent: the copy-link buttons on the page produce
// URLs like /faq-platform-faq/faq#eligibility. Keep an id stable when a question
// is only reworded, and only retire one if the question is removed.

// The current template, defined once so the intro link, the Step 1 link and the
// download button all move together when a new template version is published.
const TEMPLATE_FILE = "/documents/Template-FAQ-v2.docx";
const TEMPLATE_DOWNLOAD_NAME = "Template FAQ v2.docx";
const TEMPLATE_NOTE = "Microsoft Word document (.docx), 9 KB";
const templateLink = (text) =>
  `<a href="${TEMPLATE_FILE}" download="${TEMPLATE_DOWNLOAD_NAME}">${text}</a>`;

export const faqSummary = {
  title: "FAQ Platform",
  subtitle: "Overview",
  version: "Version 2",
  date: "September 16, 2026",
  disclaimer: `This web page describes an experimental service offered by Michael Rubenstein in his personal capacity. It is not a service of the Town of Brookline, its Moderator or Town Meeting. Questions about the platform should be directed to him.`,
  intro: `As an experiment, I am offering petitioners a platform to host a FAQ for the warrant article that they filed for consideration at the upcoming Town Meeting.

I will not exercise any editorial control of the content, but will transmit information that is provided to me according to ${templateLink('the FAQ template')}. I will try to incorporate petitioner updates as frequently as possible.`,
  // Four main steps for submitting a FAQ. `title` and `body` may contain HTML
  // links; `substeps` are plain text. Step 2's substeps began as the
  // instruction block inside "Template FAQ v1.docx" — v2 removed that block from
  // the document, so this page is now the only place they appear. Keep them in
  // step with the template by hand whenever a new version is issued.
  instructions: {
    heading: "Submitting a FAQ",
    // Stable anchor for linking straight to this section (the FAQ answer
    // "How do I submit my FAQ…" links here). Keep it stable if the heading is
    // reworded — it is part of a URL.
    anchor: "submitting-a-faq",
    lead: "Follow these four steps to submit a new or updated version of a FAQ.",
    steps: [
      {
        title: templateLink("Download the FAQ template"),
        body: `${TEMPLATE_NOTE}.`,
      },
      {
        title: "Edit the template",
        body: "Any text in the document that appears with square brackets (“[]”) needs to be replaced by the content author.",
        substeps: [
          "Replace the “[FAQ-NAME]” in the header with the name of this FAQ.",
          "Replace “[version number]” and “[Date of this version]” with this version's number and the date it was created.",
          "Replace the text of the Disclaimer with any disclaimers made by the authors of this document. The Disclaimer for the web page will be different. It will include the authors of this document and how to contact them.",
          "Replace the text of the Overview with the contents you want to appear on the Overview Tab of the web page.",
          "For each question in the FAQ, provide the question and answer by editing the text in the template. Additional questions can be added to the list.",
          "Section headings are optional. If you do not want to include sections, then delete the section names. Otherwise, replace the text “[Section Name]” with the name of each section.",
          "When updating this FAQ, create a new version with the same file name and an incremented version number. For example, if the first version is called “Outdoor Lighting FAQ v1”, then an updated version should be named “Outdoor Lighting FAQ v2”.",
        ],
      },
      {
        title: "Create an Account",
        // Authored HTML can't compute the current page, so the return path is written
        // out: this link only ever appears on the Overview tab.
        body: `If you don't have an account, <a href="/signup?next=%2Ffaq-platform-faq%2Foverview">create an account</a> now.`,
      },
      {
        title: "Submit your FAQ document",
        body: "Once you are signed in, and once I've provided you access to the Submit tab, you can upload your FAQ document and submit it to be incorporated into a web page.",
      },
    ],
  },
  // Download offered at the end of the Overview. `file` is the served path;
  // `downloadName` is the friendlier name the browser saves it under.
  template: {
    file: TEMPLATE_FILE,
    downloadName: TEMPLATE_DOWNLOAD_NAME,
    label: "Download the FAQ template",
    note: TEMPLATE_NOTE,
  }
};

export const faqData = [
  {
    id: "eligibility",
    question: "Who is eligible to create a FAQ page?",
    answer: `I will make the platform available to anyone who is filing a warrant article and wants to use it. I will let people know as I hear about warrant articles, or following the closing of the warrant.`
  },
  {
    id: "submitting-content",
    question: "How do I submit my FAQ, or a new version of it?",
    answer: `Follow <a href="/faq-platform-faq/overview#submitting-a-faq">these instructions</a>. You will need an account, and I will turn on access for you once we have agreed to host your FAQ.

Each upload is kept as a new version rather than replacing the last one, so submit again whenever you have more answers — there is no need to wait until your FAQ feels finished. The template asks you to increment the version number with each update, which makes it easier for both of us to keep track of what is current.

I am notified as soon as you upload, and I will endeavor to incorporate updates as frequently as possible.`
  },
  {
    id: "tmm-accounts",
    question: "Will TMMs have to create an account to access the FAQs?",
    answer: `No, warrant article FAQs will be public. Accounts will be needed for authors of FAQs if they want to preview the FAQ before it goes public.`
  },
  {
    id: "publicity",
    question: "How will this get publicized?",
    answer: `In coordination with petitioners, either I or they can announce when a FAQ is available on the TMMA listserv or share it with other outlets in the community. I expect that petitioners will announce updates to their FAQs as more questions get answered over time.`
  },
  {
    id: "town-tm-page-link",
    question: "Will there be a link from the Town's Town Meeting page?",
    answer: `That is up to the Moderator, I believe.`
  },
];
